import { FC, useState } from 'react';
///internal
import { Button, Checkbox, FormGroup, FormControlLabel, Alert, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Modal from '@app/components/modal/index';

import { SaveOutlined } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  WnnServiceManageTable as ManageTable,
  ManageTableGroup,
} from '@app/typings/services/winnonie/station';
import Default from '@app/constants';
import { NiuChargerStatus, UniversalChargerStatus } from '@app/typings/services/winnonie/battery';
import { EvLockerStatus } from '@app/typings/services/winnonie/evlockers';
import { SetUpperCase } from '../../getStatusColor';
import {
  getManageTableStationsWinnonie,
  setManageTableStationsWinnonie,
} from '@app/modules/winnonie/containers/stations/manageTemplate';
import _ from 'lodash';
import { isAllCheck, isIndeterminate } from '../helpers';

interface IProps {
  isShow: boolean;
  onClose: () => void;
}

interface ManageTableType {
  default: boolean;
  locker: boolean;
  niu: boolean;
  universal: boolean;
}

export const firstTable: ManageTable = {
  stationNo: true,
  station: true,
  type: true,
  address: true,
  district: false,
  province: false,
  location: false,
  flood: false,
  gas: false,
  temp: false,
  meter: true,
  status: true,
  activation: true,
  breaker: true,
  setting: true,
  total: true,
  locker: {
    empty: true,
    charging: true,
    charged: true,
    reserved: true,
    failed: true,
    inactive: true,
    pending: true,
  },
  niuCharger: {
    DISABLED: false,
    ERROR: false,
    ERR_FLAG: false,
    ERR_NORM_TO: false,
    ERR_RS485_FAULT: false,
    ERR_SLOW_TO: false,
    ERR_UNB: false,
    FAST_RECOVERY: false,
    FULL: false,
    IDLE: false,
    NORMAL_CHARGE: false,
    NO_POWER: false,
    SLOW_RECOVERY: false,
    SOFT_FULL: false,
    UNKNOWN: false,
    WARNING: false,
    WARN_FLAG: false,
    WARN_UNB: false,
  },
  universalCharger: {
    CHARGE_1: false,
    CHARGE_2: false,
    DISABLE: false,
    DOOR_CLOSE: false,
    DOOR_OPEN: false,
    ERR_BAT_FAULT: false,
    ERR_BAT_RS485_FAULT: false,
    ERR_CHG_FAULT: false,
    ERR_CHG_TIMEOUT: false,
    ERR_PCHG_TIMEOUT: false,
    ERR_RCHG_TIMEOUT: false,
    ERR_TEMP_TIMEOUT: false,
    FULL: false,
    NO_CHARGER: false,
    PRE_CHARGE: false,
    RECHARGE: false,
    R_MEAS: false,
    SOFT_FULL: false,
    UNKNOWN: false,
    WAIT_TEMP_BAT: false,
    WAIT_TEMP_CHG: false,
  },
};

const ManageTableModal: FC<IProps> = (props) => {
  const { t } = useTranslation();
  const defaultTable: ManageTable = getManageTableStationsWinnonie();
  const [manageTable, setManageTable] = useState<ManageTable>(defaultTable);
  const [checkAll, setCheckAll] = useState<ManageTableType>({
    default: isAllCheck(manageTable),
    locker: isAllCheck(manageTable.locker),
    niu: isAllCheck(manageTable.niuCharger),
    universal: isAllCheck(manageTable.universalCharger),
  });

  const onSubmit = () => {
    setManageTableStationsWinnonie(manageTable);
    props.onClose();
  };
  const onRestore = () => {
    setManageTable(Default.manageTable.winnonieService);
  };

  const onClose = () => {
    setManageTable(defaultTable);
    props.onClose();
  };

  const onSelectAll = (type: ManageTableGroup) => {
    switch (type) {
      case ManageTableGroup.DEFAULT:
        const others: any = {
          ..._.mapValues(manageTable, (value) =>
            typeof value === 'boolean' ? !checkAll.default : value,
          ),
        };
        setManageTable(others);
        setCheckAll({ ...checkAll, default: !checkAll.default });
        return;
      case ManageTableGroup.LOCKER:
        const locker = _.mapValues(manageTable.locker, () => !checkAll.locker);
        setCheckAll({ ...checkAll, locker: !checkAll.locker });
        setManageTable({ ...manageTable, locker });
        return;
      case ManageTableGroup.NIU:
        const niuCharger = _.mapValues(manageTable.niuCharger, () => !checkAll.niu);
        setCheckAll({ ...checkAll, niu: !checkAll.niu });
        setManageTable({ ...manageTable, niuCharger });
        return;
      case ManageTableGroup.UNIVERSAL:
        const universalCharger = _.mapValues(
          manageTable.universalCharger,
          () => !checkAll.universal,
        );
        setCheckAll({ ...checkAll, universal: !checkAll.universal });
        setManageTable({ ...manageTable, universalCharger });
        return;
    }
  };

  return (
    <div>
      <Modal
        isShow={props.isShow}
        title={t('user.endUser.manageTable.title')}
        primaryHide
        secondaryHide
        editWidth={1700}
        closeIconMui={onClose}
        secondaryOnClick={onClose}
        formId="addUser"
        activateBar={false}
      >
        <Alert severity="success" color="info" sx={{ borderRadius: 0 }}>
          <p className="font-bold text-xl">{t('user.endUser.manageTable.subTitle')}</p>
        </Alert>
        <Grid container justifyContent="flex-start" alignItems="center" sx={{ marginTop: 2 }}>
          <p className="font-extrabold text-lg">{t('user.endUser.manageTable.default')}</p>
          <FormControlLabel
            sx={{ marginLeft: 2 }}
            label={'Select all'}
            control={
              <Checkbox
                indeterminate={isIndeterminate(manageTable)}
                checked={isAllCheck(manageTable)}
                onClick={onSelectAll.bind(this, ManageTableGroup.DEFAULT)}
              />
            }
          />
        </Grid>
        <div className="flex">
          <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 21 }}>
            <FormControlLabel
              label="Station No."
              sx={{ minWidth: 'fit-content' }}
              control={
                <Checkbox
                  checked={manageTable.stationNo}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    stationNo: !manageTable.stationNo,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Station"
              control={
                <Checkbox
                  checked={manageTable.station}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    station: !manageTable.station,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Type"
              control={
                <Checkbox
                  checked={manageTable.type}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    type: !manageTable.type,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Address"
              control={
                <Checkbox
                  checked={manageTable.address}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    address: !manageTable.address,
                  })}
                />
              }
            />
          </FormGroup>
          <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 25 }}>
            <FormControlLabel
              label="District"
              control={
                <Checkbox
                  checked={manageTable.district}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    district: !manageTable.district,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Province"
              control={
                <Checkbox
                  checked={manageTable.province}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    province: !manageTable.province,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Location"
              control={
                <Checkbox
                  checked={manageTable.location}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    location: !manageTable.location,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Total"
              control={
                <Checkbox
                  checked={manageTable.total}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    total: !manageTable.total,
                  })}
                />
              }
            />
          </FormGroup>
          <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 16 }}>
            <FormControlLabel
              label="Floods"
              control={
                <Checkbox
                  checked={manageTable.flood}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    flood: !manageTable.flood,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Gases"
              control={
                <Checkbox
                  checked={manageTable.gas}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    gas: !manageTable.gas,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Temperatures"
              control={
                <Checkbox
                  checked={manageTable.temp}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    temp: !manageTable.temp,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Meter"
              control={
                <Checkbox
                  checked={manageTable.meter}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    meter: !manageTable.meter,
                  })}
                />
              }
            />
          </FormGroup>
          <FormGroup sx={{ marginTop: 2 }}>
            <FormControlLabel
              label="Status"
              control={
                <Checkbox
                  checked={manageTable.status}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    status: !manageTable.status,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Activation"
              control={
                <Checkbox
                  checked={manageTable.activation}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    activation: !manageTable.activation,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Breaker"
              control={
                <Checkbox
                  checked={manageTable.breaker}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    breaker: !manageTable.breaker,
                  })}
                />
              }
            />
            <FormControlLabel
              label="Setting"
              control={
                <Checkbox
                  checked={manageTable.setting}
                  onClick={setManageTable.bind(this, {
                    ...manageTable,
                    setting: !manageTable.setting,
                  })}
                />
              }
            />
          </FormGroup>
        </div>
        <Grid container justifyContent="flex-start" alignItems="center" sx={{ marginTop: 2 }}>
          <p className="font-extrabold text-lg">{t('winnonie.service.table.locker.title')}</p>
          <FormControlLabel
            sx={{ marginLeft: 2 }}
            label={'Select all'}
            control={
              <Checkbox
                indeterminate={isIndeterminate(manageTable.locker)}
                checked={isAllCheck(manageTable.locker)}
                onClick={onSelectAll.bind(this, ManageTableGroup.LOCKER)}
              />
            }
          />
        </Grid>
        <FormGroup sx={{ marginTop: 2 }}>
          <div className="flex">
            <FormGroup sx={{ paddingInlineEnd: 23 }}>
              <FormControlLabel
                label={SetUpperCase(EvLockerStatus.EMPTY)}
                control={
                  <Checkbox
                    checked={manageTable.locker.empty}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      locker: {
                        ...manageTable.locker,
                        empty: !manageTable.locker.empty,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={SetUpperCase(EvLockerStatus.CHARGING)}
                control={
                  <Checkbox
                    checked={manageTable.locker.charging}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      locker: {
                        ...manageTable.locker,
                        charging: !manageTable.locker.charging,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={SetUpperCase(EvLockerStatus.CHARGED)}
                control={
                  <Checkbox
                    checked={manageTable.locker.charged}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      locker: {
                        ...manageTable.locker,
                        charged: !manageTable.locker.charged,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2 }}>
              <FormControlLabel
                label={SetUpperCase(EvLockerStatus.RESERVED)}
                control={
                  <Checkbox
                    checked={manageTable.locker.reserved}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      locker: {
                        ...manageTable.locker,
                        reserved: !manageTable.locker.reserved,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={SetUpperCase(EvLockerStatus.FAILED)}
                control={
                  <Checkbox
                    checked={manageTable.locker.failed}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      locker: {
                        ...manageTable.locker,
                        failed: !manageTable.locker.failed,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={SetUpperCase(EvLockerStatus.INACTIVE)}
                control={
                  <Checkbox
                    checked={manageTable.locker.inactive}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      locker: {
                        ...manageTable.locker,
                        inactive: !manageTable.locker.inactive,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
          </div>
        </FormGroup>
        <Grid container justifyContent="flex-start" alignItems="center" sx={{ marginTop: 2 }}>
          <p className="font-extrabold text-lg">{t('winnonie.service.table.niuCharger.title')}</p>
          <FormControlLabel
            sx={{ marginLeft: 2 }}
            label={'Select all'}
            control={
              <Checkbox
                indeterminate={isIndeterminate(manageTable.niuCharger)}
                checked={isAllCheck(manageTable.niuCharger)}
                onClick={onSelectAll.bind(this, ManageTableGroup.NIU)}
              />
            }
          />
        </Grid>
        <FormGroup sx={{ marginTop: 2 }}>
          <div className="flex">
            <FormGroup sx={{ paddingInlineEnd: 16 }}>
              <FormControlLabel
                label={NiuChargerStatus.DISABLED}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.DISABLED}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        DISABLED: !manageTable.niuCharger.DISABLED,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.ERROR}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.ERROR}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        ERROR: !manageTable.niuCharger.ERROR,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.ERR_FLAG}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.ERR_FLAG}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        ERR_FLAG: !manageTable.niuCharger.ERR_FLAG,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.ERR_NORM_TO}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.ERR_NORM_TO}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        ERR_NORM_TO: !manageTable.niuCharger.ERR_NORM_TO,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 13 }}>
              <FormControlLabel
                label={NiuChargerStatus.ERR_RS485_FAULT}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.ERR_RS485_FAULT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        ERR_RS485_FAULT: !manageTable.niuCharger.ERR_RS485_FAULT,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.ERR_SLOW_TO}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.ERR_SLOW_TO}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        ERR_SLOW_TO: !manageTable.niuCharger.ERR_SLOW_TO,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.ERR_UNB}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.ERR_UNB}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        ERR_UNB: !manageTable.niuCharger.ERR_UNB,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.FAST_RECOVERY}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.FAST_RECOVERY}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        FAST_RECOVERY: !manageTable.niuCharger.FAST_RECOVERY,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 11 }}>
              <FormControlLabel
                label={NiuChargerStatus.FULL}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.FULL}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        FULL: !manageTable.niuCharger.FULL,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.IDLE}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.IDLE}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        IDLE: !manageTable.niuCharger.IDLE,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.NORMAL_CHARGE}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.NORMAL_CHARGE}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        NORMAL_CHARGE: !manageTable.niuCharger.NORMAL_CHARGE,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.NO_POWER}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.NO_POWER}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        NO_POWER: !manageTable.niuCharger.NO_POWER,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 7 }}>
              <FormControlLabel
                label={NiuChargerStatus.SLOW_RECOVERY}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.SLOW_RECOVERY}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        SLOW_RECOVERY: !manageTable.niuCharger.SLOW_RECOVERY,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.SOFT_FULL}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.SOFT_FULL}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        SOFT_FULL: !manageTable.niuCharger.SOFT_FULL,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.UNKNOWN}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.UNKNOWN}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        UNKNOWN: !manageTable.niuCharger.UNKNOWN,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.WARNING}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.WARNING}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        WARNING: !manageTable.niuCharger.WARNING,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2 }}>
              <FormControlLabel
                label={NiuChargerStatus.WARN_FLAG}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.WARN_FLAG}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        WARN_FLAG: !manageTable.niuCharger.WARN_FLAG,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={NiuChargerStatus.WARN_UNB}
                control={
                  <Checkbox
                    checked={manageTable.niuCharger.WARN_UNB}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      niuCharger: {
                        ...manageTable.niuCharger,
                        WARN_UNB: !manageTable.niuCharger.WARN_UNB,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
          </div>
        </FormGroup>
        <Grid container justifyContent="flex-start" alignItems="center" sx={{ marginTop: 2 }}>
          <p className="font-extrabold text-lg">
            {t('winnonie.service.table.universalCharger.title')}
          </p>
          <FormControlLabel
            sx={{ marginLeft: 2 }}
            label={'Select all'}
            control={
              <Checkbox
                indeterminate={isIndeterminate(manageTable.universalCharger)}
                checked={isAllCheck(manageTable.universalCharger)}
                onClick={onSelectAll.bind(this, ManageTableGroup.UNIVERSAL)}
              />
            }
          />
        </Grid>
        <FormGroup sx={{ marginTop: 2 }}>
          <div className="flex">
            <FormGroup sx={{ paddingInlineEnd: 17 }}>
              <FormControlLabel
                label={UniversalChargerStatus.CHARGE_1}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.CHARGE_1}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        CHARGE_1: !manageTable.universalCharger.CHARGE_1,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.CHARGE_2}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.CHARGE_2}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        CHARGE_2: !manageTable.universalCharger.CHARGE_2,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.DISABLE}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.DISABLE}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        DISABLE: !manageTable.universalCharger.DISABLE,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.DOOR_CLOSE}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.DOOR_CLOSE}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        DOOR_CLOSE: !manageTable.universalCharger.DOOR_CLOSE,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 8 }}>
              <FormControlLabel
                label={UniversalChargerStatus.DOOR_OPEN}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.DOOR_OPEN}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        DOOR_OPEN: !manageTable.universalCharger.DOOR_OPEN,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.ERR_BAT_FAULT}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.ERR_BAT_FAULT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        ERR_BAT_FAULT: !manageTable.universalCharger.ERR_BAT_FAULT,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.ERR_BAT_RS485_FAULT}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.ERR_BAT_RS485_FAULT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        ERR_BAT_RS485_FAULT: !manageTable.universalCharger.ERR_BAT_RS485_FAULT,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.ERR_CHG_FAULT}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.ERR_CHG_FAULT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        ERR_CHG_FAULT: !manageTable.universalCharger.ERR_CHG_FAULT,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 8 }}>
              <FormControlLabel
                label={UniversalChargerStatus.ERR_CHG_TIMEOUT}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.ERR_CHG_TIMEOUT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        ERR_CHG_TIMEOUT: !manageTable.universalCharger.ERR_CHG_TIMEOUT,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.ERR_PCHG_TIMEOUT}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.ERR_PCHG_TIMEOUT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        ERR_PCHG_TIMEOUT: !manageTable.universalCharger.ERR_PCHG_TIMEOUT,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.ERR_RCHG_TIMEOUT}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.ERR_RCHG_TIMEOUT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        ERR_RCHG_TIMEOUT: !manageTable.universalCharger.ERR_RCHG_TIMEOUT,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.ERR_TEMP_TIMEOUT}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.ERR_TEMP_TIMEOUT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        ERR_TEMP_TIMEOUT: !manageTable.universalCharger.ERR_TEMP_TIMEOUT,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 11 }}>
              <FormControlLabel
                label={UniversalChargerStatus.FULL}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.FULL}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        FULL: !manageTable.universalCharger.FULL,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.NO_CHARGER}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.NO_CHARGER}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        NO_CHARGER: !manageTable.universalCharger.NO_CHARGER,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.PRE_CHARGE}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.PRE_CHARGE}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        PRE_CHARGE: !manageTable.universalCharger.PRE_CHARGE,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.RECHARGE}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.RECHARGE}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        RECHARGE: !manageTable.universalCharger.RECHARGE,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2, paddingInlineEnd: 9 }}>
              <FormControlLabel
                label={UniversalChargerStatus.R_MEAS}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.R_MEAS}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        R_MEAS: !manageTable.universalCharger.R_MEAS,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.SOFT_FULL}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.SOFT_FULL}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        SOFT_FULL: !manageTable.universalCharger.SOFT_FULL,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.UNKNOWN}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.UNKNOWN}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        UNKNOWN: !manageTable.universalCharger.UNKNOWN,
                      },
                    })}
                  />
                }
              />
              <FormControlLabel
                label={UniversalChargerStatus.WAIT_TEMP_BAT}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.WAIT_TEMP_BAT}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        WAIT_TEMP_BAT: !manageTable.universalCharger.WAIT_TEMP_BAT,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
            <FormGroup sx={{ marginTop: 2 }}>
              <FormControlLabel
                label={UniversalChargerStatus.WAIT_TEMP_CHG}
                control={
                  <Checkbox
                    checked={manageTable.universalCharger.WAIT_TEMP_CHG}
                    onClick={setManageTable.bind(this, {
                      ...manageTable,
                      universalCharger: {
                        ...manageTable.universalCharger,
                        WAIT_TEMP_CHG: !manageTable.universalCharger.WAIT_TEMP_CHG,
                      },
                    })}
                  />
                }
              />
            </FormGroup>
          </div>
        </FormGroup>
        <div className="flex justify-end">
          <Button variant="text" sx={{ marginTop: 5, marginRight: 3 }} onClick={onRestore}>
            <u className=" text-lg">{t('user.endUser.button.restore')}</u>
          </Button>
          <LoadingButton
            variant="contained"
            startIcon={<SaveOutlined />}
            loading={false}
            loadingPosition="start"
            sx={{ width: 150, marginTop: 5 }}
            onClick={onSubmit}
            size="large"
          >
            {t('general.button.save')}
          </LoadingButton>
        </div>
      </Modal>
    </div>
  );
};

export default ManageTableModal;
